// System builder. Turns a StarSystemData record into a live Three.js
// Group of objects, ready to drop into the scene. The returned handle
// exposes a tick() for orbits + rotations and a dispose() for cleanup
// when the player leaves a procedural system.
//
// Stars render through the procedural photosphere shader with a
// camera-facing glow quad, typed by STAR_TYPES (src/systems/startypes.js).
// Solid planets render through the procedural planet shader (archetypes
// in src/shaders/proceduralPlanet.js), giants through the procedural gas
// giant shader, with optional atmosphere rim shells and ring systems.
// Every surface is lit by its host star's colour along a world-space sun
// direction that tick() refreshes. Planets route through hostStarId so
// multi-star systems work without schema changes.

import * as THREE from 'three';
import {
  proceduralGasGiantVertexShader,
  proceduralGasGiantFragmentShader
} from '../shaders/proceduralGasGiant.js';
import {
  proceduralPlanetVertexShader,
  proceduralPlanetFragmentShader,
  atmosphereShellVertexShader,
  atmosphereShellFragmentShader,
  proceduralRingVertexShader,
  proceduralRingFragmentShader,
  PLANET_ARCHETYPES
} from '../shaders/proceduralPlanet.js';
import {
  proceduralStarVertexShader,
  proceduralStarFragmentShader,
  starGlowVertexShader,
  starGlowFragmentShader
} from '../shaders/proceduralStar.js';
import { createGargantua } from '../gargantua.js';
import { blackbodyRgb, starLightRgb } from './blackbody.js';
import { STAR_TYPES, inferStarType } from './startypes.js';

/**
 * Build a system Group from a data record.
 *
 * @param {import('./schema.js').StarSystemData} data
 * @param {Object} [opts]
 * @param {() => THREE.Texture|null} [opts.getSkyboxTexture] Lazy accessor
 *   for the scene's panorama; passed into shaders that lens the
 *   background (currently just the Gargantua disk raymarcher).
 * @param {THREE.WebGLRenderer} [opts.renderer]
 * @returns {{
 *   root: THREE.Group,
 *   bodies: Record<string, THREE.Object3D>,
 *   data: import('./schema.js').StarSystemData,
 *   tick: (delta:number, elapsed:number) => void,
 *   dispose: () => void
 * }}
 */
export function buildSystem(data, opts = {}) {
  const root = new THREE.Group();
  root.name = `system:${data.id}`;
  root.userData.systemId = data.id;
  root.userData.systemData = data;

  /** @type {Record<string, THREE.Object3D>} */
  const bodies = {};
  // Procedural-surface meshes that need uTime + uSunDir refreshed
  // each frame. Populated as buildPlanet creates ShaderMaterial-based
  // bodies. The tick() function walks this list to push the world-
  // space sun direction into each material's uniforms.
  const proceduralSurfaces = [];
  // Black holes need their own per-frame uniform refresh (uTime,
  // uBhWorldPos, deferred skybox pickup). Collected here so the
  // tick() loop can drive them without scanning all bodies.
  const blackHoles = [];

  // Light colour each star casts on its planets (linear RGB).
  const starLight = {};
  const outermost = data.planets.reduce((m, p) => Math.max(m, p.distance || 0), 0);
  for (const star of data.stars) {
    const group = star.type === 'BlackHole'
      ? buildBlackHole(star, blackHoles, opts)
      : buildStar(star, proceduralSurfaces, outermost);
    bodies[star.id] = group;
    root.add(group);
    starLight[star.id] = star.type === 'BlackHole'
      ? [1, 0.92, 0.85]
      : starLightRgb(star.temperature || 5772);
  }

  for (const planet of data.planets) {
    const light = starLight[planet.hostStarId] || [1, 1, 1];
    const planetGroup = buildPlanet(planet, proceduralSurfaces, light);
    bodies[planet.id] = planetGroup;
    planetGroup.userData.hostStarId = planet.hostStarId;
    const host = bodies[planet.hostStarId] || root;
    host.add(planetGroup);

    if (typeof planet.distance === 'number') {
      planetGroup.position.set(planet.distance, 0, 0);
    }
  }

  if (data.scenePosition) {
    root.position.set(data.scenePosition.x, data.scenePosition.y, data.scenePosition.z);
  }

  // Scratch vectors for the per-frame procedural-surface sun-direction
  // update so the hot path doesn't allocate.
  const _proSunWorld = new THREE.Vector3();
  const _proBodyWorld = new THREE.Vector3();
  const _proSunDir = new THREE.Vector3();

  return {
    root,
    bodies,
    data,
    tick(delta, elapsed) {
      // Planet orbits — parented to host star, so we move in host-local
      // space and the binary-orbiting star transform composes naturally.
      for (const planet of data.planets) {
        const obj = bodies[planet.id];
        if (!obj) continue;
        const t = elapsed * planet.orbitalSpeed;
        const a = planet.distance;
        const e = planet.eccentricity || 0;
        // Simple parametric ellipse; not Keplerian but visually fine
        // at the artistic scales we're using here.
        obj.position.x = a * Math.cos(t);
        obj.position.z = a * Math.sin(t) * Math.sqrt(Math.max(0, 1 - e * e));
        if (planet.orbitalInclination) {
          obj.position.y = obj.position.z * Math.sin(planet.orbitalInclination);
        }
        obj.rotation.y += (planet.rotationSpeed || 0) * delta * 0.5;
      }

      // Procedural-surface bodies (sub-Neptunes, gas giants) need
      // their shader uniforms refreshed each frame so the band drift
      // animates and the lit hemisphere tracks the host star. The
      // sun direction is computed in WORLD space — we look up the
      // host star's world position (which already accounts for any
      // binary orbit motion) so the terminator stays anchored even
      // as the star moves in its own orbit.
      for (const entry of proceduralSurfaces) {
        if (!entry || !entry.uniforms) continue;
        entry.uniforms.uTime.value = elapsed * (entry.timeScale || 1);
        if (entry.isStar) continue;
        const hostStar = bodies[entry.hostStarId];
        if (hostStar) {
          hostStar.getWorldPosition(_proSunWorld);
          entry.mesh.getWorldPosition(_proBodyWorld);
          _proSunDir.subVectors(_proSunWorld, _proBodyWorld);
          if (_proSunDir.lengthSq() > 1e-6) {
            _proSunDir.normalize();
            entry.uniforms.uSunDir.value.copy(_proSunDir);
            // Atmosphere shell and rings share the direction; rings also
            // need the planet centre for their shadow cylinder.
            if (entry.linked) {
              for (const u of entry.linked) {
                u.uSunDir.value.copy(_proSunDir);
                if (u.uPlanetCenter) u.uPlanetCenter.value.copy(_proBodyWorld);
              }
            }
          }
        }
        // Slow body spin so the procedural pattern visibly rotates
        // relative to its star without spinning so fast it reads
        // as a propeller.
        entry.mesh.rotation.y += delta * (entry.spinRate || 0.03);
      }

      // Black-hole raymarchers: push uTime + world position into the
      // shader. The skybox accessor is consulted here too so the
      // lensing picks up the panorama once it finishes loading.
      for (const bh of blackHoles) {
        bh.tick(elapsed);
      }

      // Binary / hierarchical star orbits. Stars with binaryOrbit
      // move around their host star group. The host star itself
      // stays at its position field, so the bary-center drifts a bit
      // from the true value but reads correct visually.
      for (const star of data.stars) {
        if (!star.binaryOrbit) continue;
        const obj = bodies[star.id];
        if (!obj) continue;
        const t = elapsed * star.binaryOrbit.orbitalSpeed;
        const a = star.binaryOrbit.semiMajorAxis;
        const e = star.binaryOrbit.eccentricity || 0;
        // Lateral compression by sqrt(1 - e^2) gives a proper-looking
        // ellipse, with the host approximately at one focus.
        obj.position.x = a * Math.cos(t);
        obj.position.z = a * Math.sin(t) * Math.sqrt(Math.max(0, 1 - e * e));
      }
    },
    dispose() {
      root.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      if (root.parent) root.parent.remove(root);
    }
  };
}

/**
 * Build a single star: procedural photosphere (temperature-driven
 * granulation, spots, limb darkening), an additive camera-facing glow
 * quad, and a point light in the star's colour. Radiance stays above
 * 1.0 so the bloom pass haloes it the way it does Sol.
 */
function buildStar(starData, proceduralSurfaces, outermost = 0) {
  const group = new THREE.Group();
  group.name = starData.name;
  group.userData.isStar = true;
  group.userData.bodyId = starData.id;
  group.userData.data = starData;
  // Flat display fields the main viewer reads off object.userData
  // (focus zoom math needs radius; the rest feed the nav label and
  // science panel). See the matching note in buildPlanet.
  group.userData.radius = starData.radius;
  group.userData.name = starData.name;
  group.userData.type = starData.type;
  group.userData.realDiameter = starData.realDiameter;

  const temperature = starData.temperature || 5772;
  const starType = STAR_TYPES[starData.starType] ? starData.starType : inferStarType(temperature, starData.radius);
  const look = STAR_TYPES[starType];
  group.userData.starType = starType;

  // Surface colour: blackbody, saturated a little past the physical
  // value (pow) so a red dwarf reads red on screen rather than peach.
  const sat = (t) => blackbodyRgb(t).map((v) => Math.pow(v, 1.5));
  let base = sat(temperature);
  let cool = sat(temperature - 800);
  let hot = sat(temperature + 900);
  if (starType === 'brown-dwarf') {
    // Methane and alkali absorption push brown dwarfs toward magenta.
    const tint = (c, k) => c.map((v, i) => v * (1 - k) + [0.5, 0.12, 0.3][i] * k);
    base = tint(base, 0.5); cool = tint(cool, 0.65); hot = tint(hot, 0.35);
  }
  const seed = seedFromString(`${starData.id}:${starData.name}`);

  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(...base) },
      uCoolColor: { value: new THREE.Color(...cool) },
      uHotColor: { value: new THREE.Color(...hot) },
      uGranScale: { value: look.gran },
      uSpots: { value: look.spots },
      uFlare: { value: look.flare },
      uLimb: { value: look.limb },
      uBanded: { value: look.banded },
      uIntensity: { value: look.intensity },
      uSeed: { value: new THREE.Vector3(seed[0], seed[1], seed[2]) }
    },
    vertexShader: proceduralStarVertexShader,
    fragmentShader: proceduralStarFragmentShader
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(starData.radius, 96, 48), mat);
  mesh.userData.isStarMesh = true;
  group.add(mesh);
  if (proceduralSurfaces) {
    proceduralSurfaces.push({ mesh, uniforms: mat.uniforms, isStar: true, timeScale: 1 });
  }

  const glowColor = hot.map((v, i) => (v + base[i]) * 0.5);
  const glowMat = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(...glowColor) },
      uSize: { value: starData.radius * look.glow },
      uCore: { value: 1 / look.glow },
      uStrength: { value: look.glowStrength },
      uRays: { value: look.rays },
      uTime: { value: 0 },
      uSeed: { value: seed[0] }
    },
    vertexShader: starGlowVertexShader,
    fragmentShader: starGlowFragmentShader,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });
  const glow = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), glowMat);
  glow.name = 'starGlow';
  glow.frustumCulled = false;
  glow.renderOrder = 2;
  glow.raycast = () => {};
  group.add(glow);
  if (proceduralSurfaces) {
    proceduralSurfaces.push({ mesh: glow, uniforms: glowMat.uniforms, isStar: true, timeScale: 1 });
  }

  // Local illumination for MeshStandard bodies (authored planets). The
  // procedural surfaces light themselves from uSunDir/uSunColor.
  const light = new THREE.PointLight(new THREE.Color(...starLightRgb(temperature)), look.light, Math.max(8000, outermost * 2.5), 1.0);
  group.add(light);

  if (starData.position) {
    group.position.set(starData.position.x, starData.position.y, starData.position.z);
  }

  return group;
}

/**
 * Build a Gargantua-style black hole as the system's primary. Returns
 * a group with the same userData-shape contract as buildStar so
 * hostStarId lookups work for orbiting planets. The raymarcher's
 * tick handle is pushed into the shared blackHoles list so the
 * system's tick() can drive it each frame.
 */
function buildBlackHole(starData, blackHoles, opts) {
  const cfg = starData.gargantuaConfig || {};
  const handle = createGargantua({
    rsWorld: cfg.rsWorld,
    diskInner: cfg.diskInner,
    diskOuter: cfg.diskOuter,
    exposure: cfg.exposure,
    getSkyboxTexture: opts.getSkyboxTexture,
    renderer: opts.renderer
  });

  const group = handle.object;
  group.userData = {
    ...group.userData,
    isStar: true,             // satisfies hostStarId routing
    bodyId: starData.id,
    data: starData,
  };

  if (starData.position) {
    group.position.set(starData.position.x, starData.position.y, starData.position.z);
  }

  blackHoles.push(handle);
  return group;
}

/**
 * Build a single planet. Giants (`proceduralGasGiant`) use the gas
 * giant band shader (same one that drives Nibiru / Uranus / Neptune in
 * Sol), palette tinted by the host star's light. Everything else uses
 * the procedural planet shader with the archetype in `surface`; authored
 * planets without one get an archetype inferred from their colours and
 * atmosphere flag. Optional atmosphere rim shell and ring system. Each
 * shader surface is registered in proceduralSurfaces so tick() can push
 * uTime + the sun direction.
 */
function buildPlanet(planetData, proceduralSurfaces, lightRgb = [1, 1, 1]) {
  const group = new THREE.Group();
  group.name = planetData.name;
  group.userData.isPlanet = true;
  group.userData.bodyId = planetData.id;
  group.userData.data = planetData;
  // Mirror the flat display fields the main viewer reads off
  // object.userData directly (focusOnObject's zoom-distance math,
  // the science panel, the nav label). Without userData.radius the
  // focus distance computes radius*zoom = undefined*5 = NaN, which
  // poisons camera.position and blacks out the whole viewport. The
  // Sol pipeline sets these on its planet groups; remote systems
  // build their own groups here, so they have to set them too.
  group.userData.radius = planetData.radius;
  group.userData.name = planetData.name;
  group.userData.type = planetData.type;
  group.userData.realDiameter = planetData.realDiameter;
  group.userData.realDistance = planetData.realDistance;
  group.userData.orbitalPeriod = planetData.orbitalPeriod;

  const radius = planetData.radius;
  const geom = new THREE.SphereGeometry(radius, 128, 64);
  const sunColor = new THREE.Color(lightRgb[0], lightRgb[1], lightRgb[2]);
  const linked = [];

  let mesh;
  let entry;
  if (planetData.proceduralGasGiant) {
    const cfg = planetData.proceduralGasGiant;
    // The band shader has no light-colour input; bake the star's light
    // into the palette instead (lighter touch than full multiply, the
    // band contrast is the point).
    const tint = new THREE.Color(1, 1, 1).lerp(sunColor, 0.7);
    const pal = (hex) => new THREE.Color(hex).multiply(tint);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime:         { value: 0 },
        uSunDir:       { value: new THREE.Vector3(-1, 0, 0) },
        uPaletteDeep:  { value: pal(cfg.paletteDeep  || 0x2a1408) },
        uPaletteBelt:  { value: pal(cfg.paletteBelt  || 0x8a3a18) },
        uPaletteZone:  { value: pal(cfg.paletteZone  || 0xd4946a) },
        uPaletteStorm: { value: pal(cfg.paletteStorm || cfg.paletteBelt || 0x701810) },
        uBandCount:    { value: cfg.bandCount != null ? cfg.bandCount : 16.0 },
        uTurbulence:   { value: cfg.turbulence != null ? cfg.turbulence : 1.2 },
        uStormLatDeg:  { value: cfg.stormLatDeg != null ? cfg.stormLatDeg : 0 },
        uStormLonDeg:  { value: cfg.stormLonDeg != null ? cfg.stormLonDeg : 0 },
        uStormSize:    { value: cfg.stormSize != null ? cfg.stormSize : 0.001 },
        uLimbDarken:   { value: cfg.limbDarken != null ? cfg.limbDarken : 0.85 },
        uRimGlow:      { value: cfg.rimGlow != null ? cfg.rimGlow : 0.55 },
        uCloudDetailStrength: { value: cfg.cloudDetailStrength != null ? cfg.cloudDetailStrength : 1.0 },
        uCloudDetailFreq:     { value: cfg.cloudDetailFreq != null ? cfg.cloudDetailFreq : 7.0 },
        uBandChaos:           { value: cfg.bandChaos != null ? cfg.bandChaos : 0.5 },
        uSunIntensity:        { value: 1.0 }
      },
      vertexShader: proceduralGasGiantVertexShader,
      fragmentShader: proceduralGasGiantFragmentShader,
      side: THREE.FrontSide,
      transparent: false
    });
    mesh = new THREE.Mesh(geom, mat);
    entry = {
      mesh,
      uniforms: mat.uniforms,
      hostStarId: planetData.hostStarId,
      timeScale: cfg.timeScale != null ? cfg.timeScale : 0.25,
      spinRate: cfg.spinRate != null ? cfg.spinRate : 0.03
    };
  } else {
    const surface = planetData.surface || inferSurface(planetData);
    const pal = paletteColors(surface.palette);
    const atmo = planetData.atmosphere || null;
    const seed = surface.seed || seedFromString(`${planetData.id}:${planetData.name}`);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uSunDir: { value: new THREE.Vector3(-1, 0, 0) },
        uSunColor: { value: sunColor.clone() },
        uSunIntensity: { value: 1.0 },
        uArchetype: { value: PLANET_ARCHETYPES[surface.archetype] ?? 0 },
        uSeed: { value: new THREE.Vector3(seed[0], seed[1], seed[2]) },
        uRadius: { value: radius },
        uC0: { value: pal[0] }, uC1: { value: pal[1] }, uC2: { value: pal[2] },
        uC3: { value: pal[3] }, uC4: { value: pal[4] }, uC5: { value: pal[5] },
        uSeaLevel: { value: surface.seaLevel || 0 },
        uCraterDensity: { value: surface.craterDensity || 0 },
        uIceCap: { value: surface.iceCap || 0 },
        uCloudCover: { value: surface.cloudCover || 0 },
        uCloudColor: { value: new THREE.Color(surface.cloudColor || '#f4f6f8') },
        uBump: { value: surface.bump != null ? surface.bump : 1 },
        uFeature: { value: surface.feature || 1 },
        uGlow: { value: surface.glow || 0 },
        uAtmoColor: { value: new THREE.Color(atmo ? atmo.color : '#000000') },
        uAtmoDensity: { value: atmo ? atmo.density : 0 }
      },
      vertexShader: proceduralPlanetVertexShader,
      fragmentShader: proceduralPlanetFragmentShader
    });
    mesh = new THREE.Mesh(geom, mat);
    entry = {
      mesh,
      uniforms: mat.uniforms,
      hostStarId: planetData.hostStarId,
      timeScale: 1,
      // Solid worlds turn slowly under their drifting clouds.
      spinRate: 0.012
    };
  }
  mesh.userData.isProceduralSurface = true;
  group.add(mesh);

  // Atmosphere rim: explicit `atmosphere`, or the older hasAtmosphere
  // flags authored systems use.
  const atmo = planetData.atmosphere
    || (planetData.hasAtmosphere ? {
      color: planetData.atmosphereColor != null ? planetData.atmosphereColor : 0x82aeba,
      scale: planetData.atmosphereScale || 1.03,
      strength: planetData.atmosphereStrength != null ? planetData.atmosphereStrength : 0.7
    } : null);
  if (atmo) {
    const shellR = radius * Math.max(1.012, atmo.scale || 1.03);
    const u = {
      uSunDir: { value: new THREE.Vector3(-1, 0, 0) },
      uSunColor: { value: sunColor.clone() },
      uAtmoColor: { value: new THREE.Color(atmo.color) },
      uPlanetRadius: { value: radius },
      uShellRadius: { value: shellR },
      uStrength: { value: atmo.strength != null ? atmo.strength : 0.7 },
      uFalloff: { value: 2.2 }
    };
    const shell = new THREE.Mesh(
      new THREE.SphereGeometry(shellR, 96, 48),
      new THREE.ShaderMaterial({
        uniforms: u,
        vertexShader: atmosphereShellVertexShader,
        fragmentShader: atmosphereShellFragmentShader,
        side: THREE.BackSide,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      })
    );
    shell.name = 'atmosphereShell';
    // Clicks and the flight clearance should see the planet, not its air.
    shell.raycast = () => {};
    group.add(shell);
    linked.push(u);
  }

  if (planetData.rings) {
    const r = planetData.rings;
    const [ca, cb] = paletteColors(r.colors || ['#d8c8a8', '#8a7a64']);
    const gaps = r.gaps || [0.55, 0.85, 0.02];
    const u = {
      uInner: { value: r.inner },
      uOuter: { value: r.outer },
      uColA: { value: ca.multiply(sunColor) },
      uColB: { value: cb.multiply(sunColor) },
      uSunDir: { value: new THREE.Vector3(-1, 0, 0) },
      uSunColor: { value: new THREE.Color(1, 1, 1) },
      uPlanetCenter: { value: new THREE.Vector3() },
      uPlanetRadius: { value: radius },
      uOpacity: { value: r.opacity != null ? r.opacity : 0.8 },
      uSeed: { value: r.seed || 0 },
      uGaps: { value: new THREE.Vector3(gaps[0], gaps[1], gaps[2]) }
    };
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(r.inner, r.outer, 256, 1),
      new THREE.ShaderMaterial({
        uniforms: u,
        vertexShader: proceduralRingVertexShader,
        fragmentShader: proceduralRingFragmentShader,
        side: THREE.DoubleSide,
        transparent: true,
        depthWrite: false
      })
    );
    ring.name = 'planetRings';
    ring.rotation.x = -Math.PI / 2;
    ring.raycast = () => {};
    group.add(ring);
    linked.push(u);
  }

  if (linked.length) entry.linked = linked;
  if (proceduralSurfaces) proceduralSurfaces.push(entry);

  // Apply axial tilt to the visual group, leaving the orbit motion
  // in the parent group untouched.
  if (planetData.tilt) {
    group.rotation.z = THREE.MathUtils.degToRad(planetData.tilt);
  }

  return group;
}

// Six palette slots as linear THREE.Colors; short palettes repeat
// their last colour.
function paletteColors(list = []) {
  const src = list.length ? list : ['#9090a0'];
  const out = [];
  for (let i = 0; i < 6; i++) out.push(new THREE.Color(src[Math.min(i, src.length - 1)]));
  return out;
}

// Stable pseudo-random offset from a string, for noise seeds.
function seedFromString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const f = (k) => (((h ^ (h >>> k)) >>> 0) % 9973) / 9973 * 97;
  return [f(3), f(11), f(19)];
}

// Archetype for authored planets that predate `surface`: pale bodies are
// ice, bodies with air read as dusty deserts, the rest as cratered rock.
function inferSurface(planetData) {
  const colors = planetData.colors && planetData.colors.length ? planetData.colors : ['#9090a0', '#606070'];
  const c0 = new THREE.Color(colors[0]);
  const hsl = c0.getHSL({});
  const c1 = colors[1] || colors[0];
  if (hsl.l > 0.55) {
    return { archetype: 'ice', palette: [colors[0], c1, '#8a5a4a', '#3a70a8'], feature: 1, bump: 1 };
  }
  if (planetData.hasAtmosphere) {
    return {
      archetype: 'desert',
      palette: [colors[0], c1, '#' + c0.clone().multiplyScalar(0.6).getHexString(), c1, '#f0eeea'],
      craterDensity: 0.25, iceCap: 0.06, feature: 1, bump: 1.2
    };
  }
  return {
    archetype: 'cratered',
    palette: [colors[0], c1, '#' + c0.clone().lerp(new THREE.Color(1, 1, 1), 0.35).getHexString()],
    craterDensity: 0.7, bump: 1.5
  };
}
